import { useContext, Component, ContextType } from 'react';

import galleryStyles from '../article.module.css';

import { ArticleContext } from '../Article.context';
import { Caption } from './Caption';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { EffectFade, Navigation, Pagination } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export const StyledGallery = (props: Readonly<StyledGalleryProps>) => {
	const {
		id,
		images,
		captionenabled,
		caption,
		expandfullwidth,
		animation,
		autoplay,
		direction,
		bleed,
		control_speed,
		styles,
	} = props;
	const { lang } = useContext(ArticleContext);
	let captionText: any = caption;
	if (typeof caption === 'object') {
		captionText = caption[lang] ? caption[lang] : '';
	}

	const galleryImages = images.map((image) => {
		if (image.caption[lang]) {
			image.caption = image.caption[lang];
		}
		return image;
	});

	return (
		<Gallery
			id={id}
			bleed={bleed === 'on'}
			caption={captionText}
			images={galleryImages}
			controlSpeed={control_speed}
			styles={styles}
			captionEnabled={captionenabled === 'on'}
			expandFullWidth={expandfullwidth === 'on'}
			animation={animation}
			autoplay={autoplay === 'on'}
			direction={direction}
		/>
	);
};

interface StyledGalleryProps {
	id: string;
	images: Array<any>;
	styles: any;
	captionenabled: string;
	caption: string | any;
	expandfullwidth: string;
	animation: string;
	autoplay: string;
	direction: any;
	bleed: string;
	control_speed: string;
}

export class Gallery extends Component<GalleryProps, any> {
	static contextType = ArticleContext;
	declare context: ContextType<typeof ArticleContext>;

	public static defaultProps = {
		id: '',
		caption: '',
		captionEnabled: false,
		direction: 'horizontal',
		controlSpeed: 'medium',
	};

	interval: any = null;
	state: any = {
		index: null,
		swiper: null,
	};
	componentDidMount() {
		const { images, autoplay, controlSpeed } = this.props;
		const total = images.length;
		this.setState({
			index: 0,
			total,
		});
		if (autoplay) {
			let speed = 0;
			switch (controlSpeed) {
				case 'slow':
					speed = 11000;
					break;
				case 'medium':
					speed = 7000;
					break;
				case 'fast':
					speed = 3000;
					break;
				case 'vfast':
					speed = 1500;
					break;
				default:
					speed = 5000;
					break;
			}

			this.interval = setInterval(this.onTic, speed);
		}
	}

	componentDidUpdate(prevProps: GalleryProps, prevState: any) {
		const { swiper } = this.state;

		if (swiper && prevProps.direction !== this.props.direction) {
			swiper.changeDirection(this.props.direction);
		}

		if (prevState.swipper === null && swiper) {
			console.log(`I trigger the re render`);
			setTimeout(() => {
				swiper.updateSize();
			}, 500);
		}
	}

	componentWillUnmount() {
		if (this.interval) {
			clearInterval(this.interval);
		}
	}

	onTic = () => {
		const { autoplay } = this.props;
		const { total, swiper } = this.state;
		let { index } = this.state;
		index = index + 1;
		if (!autoplay) {
			return;
		}

		if (index >= total) {
			this.setState({ index: 0 });
			swiper.slideTo(0, 200);
			return;
		}
		this.setState({ index });
		swiper.slideNext(200);
	};

	onSlideChange = ({ realIndex }: any) => {
		this.setState({ index: realIndex });
	};

	render() {
		const {
			id,
			images,
			captionEnabled,
			caption,
			animation,
			bleed,
			styles,
			direction,
		} = this.props;

		const { lang } = this.context;

		const dependencies = [Navigation, Pagination];
		if (animation === 'fade') {
			dependencies.push(EffectFade);
		}
		const { index } = this.state;
		const className = [galleryStyles['gallery'], 'media', 'gallery'];
		if (bleed) {
			className.push('bleed');
		}

		let containerStyle = {};
		if (styles) {
			containerStyle = applyStyles(styles, containerStyle);
		}

		return (
			<div id={id} style={containerStyle} className={className.join(' ')}>
				<Swiper
					effect={animation}
					modules={dependencies}
					navigation={true}
					slidesPerView={'auto'}
					updateOnWindowResize={true}
					pagination={{
						clickable: true,
					}}
					onSlideChange={this.onSlideChange}
					onSwiper={(swiper: SwiperClass) => {
						swiper.changeDirection(direction);
						setTimeout(() => {
							swiper.update();
						}, 1000);
						this.setState({
							swiper,
						});
					}}
				>
					{images.map((image, i) => {
						const { imageurl, caption } = image;
						const slideClassesName = [
							galleryStyles['gallery-slider'],
						];
						if (i !== index) {
							slideClassesName.push(galleryStyles['hide']);
						}

						let captionText = '';
						if (typeof caption === 'string') {
							captionText = caption;
						} else if (typeof caption === 'object') {
							captionText = caption[lang];
						}

						return (
							<SwiperSlide
								key={i}
								virtualIndex={i}
								className={slideClassesName.join(' ')}
							>
								<div className={galleryStyles['gallery-image']}>
									<img src={imageurl} alt={captionText} />
									<Caption content={caption} />
								</div>
							</SwiperSlide>
						);
					})}
				</Swiper>
				{captionEnabled && caption ? (
					<Caption content={caption} />
				) : null}
			</div>
		);
	}
}

interface GalleryProps {
	id: string;
	images: Array<any>;
	styles: any;
	captionEnabled: boolean;
	caption: string;
	expandFullWidth: boolean;
	animation: any;
	autoplay: boolean;
	bleed: boolean;
	direction: 'horizontal' | 'vertical' | undefined;
	controlSpeed: string;
}

function applyStyles(styles: any, containerStyle: any) {
	containerStyle = clone(containerStyle);
	styles = clone(styles);
	const {
		unit,
		margin_bottom,
		margin_top,
		margin_left,
		margin_right,
		padding_left,
		padding_right,
		padding_top,
		padding_bottom,
	} = styles;
	if (margin_bottom !== undefined && unit['margin_bottom']) {
		containerStyle.marginBottom = `${margin_bottom}${unit['margin_bottom']}`;
	}
	if (margin_top !== undefined && unit['margin_top']) {
		containerStyle.marginTop = `${margin_top}${unit['margin_top']}`;
	}
	if (margin_left !== undefined && unit['margin_left']) {
		containerStyle.marginLeft = `${margin_left}${unit['margin_left']}`;
	}
	if (margin_right !== undefined && unit['margin_right']) {
		containerStyle.marginRight = `${margin_right}${unit['margin_right']}`;
	}

	if (padding_bottom !== undefined && unit['padding_bottom']) {
		containerStyle.paddingBottom = `${padding_bottom}${unit['padding_bottom']}`;
	}
	if (padding_top !== undefined && unit['padding_top']) {
		containerStyle.paddingTop = `${padding_top}${unit['padding_top']}`;
	}
	if (padding_left !== undefined && unit['padding_left']) {
		containerStyle.paddingLeft = `${margin_left}${unit['padding_left']}`;
	}
	if (padding_right !== undefined && unit['padding_right']) {
		containerStyle.paddingRight = `${margin_right}${unit['padding_right']}`;
	}

	return containerStyle;
}

function clone(obj: any) {
	if (structuredClone) {
		return structuredClone(obj);
	}
	return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}
